import * as contentTypes from '../../enums/contentTypes';
import type {Course} from '../course/courseTypes';
import type {Guide} from '../guide/guideTypes';
import type {RootState} from '../store';
import {createSelector} from '../util';
import type {KnowledgeHubSection} from './knowledgeHubTypes';

type ToJSType<T> = {toJS: () => T};

export const getKnowledgeHubById = (
  state: RootState,
  knowledgeHubId: number
) => {
  const knowledgeHub = state.knowledgeHub
    .get('knowledgeHubs')
    .find((kh: any) => kh.get('id') === knowledgeHubId);
  return knowledgeHub ? knowledgeHub.toJS() : null;
};

export const getSectionById = (state: RootState, sectionId: number) => {
  const section = state.knowledgeHub.getIn(['sectionsById', sectionId]);
  return section ? section.toJS() : null;
};

export const getHasSingleV2KnowledgeHub = (state: RootState) => {
  const knowledgeHubs = state.knowledgeHub.get('knowledgeHubs');
  if (knowledgeHubs.size !== 1) {
    return false;
  }

  const isManualAssignment =
    knowledgeHubs.get(0).get('contentAssignment') === 'manual';
  const isManagementViewEnabled =
    state.featureFlags.get('customerFlags').knowledgeHubsManagementEnabled;

  return isManualAssignment && isManagementViewEnabled;
};

const selectKnowledgeHubSections = createSelector(
  [
    (state) =>
      state.knowledgeHub.get('sectionsById') as ToJSType<
        Record<number, KnowledgeHubSection>
      >,
    (_, knowledgeHubId: number) => knowledgeHubId,
  ],
  (sectionsById, knowledgeHubId) => {
    const allSections = Object.values(sectionsById.toJS());
    return allSections
      .filter((section) => section.knowledgeHubId === knowledgeHubId)
      .sort((a, b) => a.position - b.position);
  }
);

export const selectKnowledgeHubSectionsWithContent = createSelector(
  [
    selectKnowledgeHubSections,
    (state) => state.course.get('courses') as ToJSType<Course[]>,
    (state) => state.guide.get('guides') as ToJSType<Guide[]>,
  ],
  (sections, courses, guides) => {
    const getContentData = ({
      id,
      contentType,
    }: KnowledgeHubSection['content'][number]) => {
      const list =
        contentType === contentTypes.GUIDE ? guides.toJS() : courses.toJS();
      const content = list.find((item) => item?.id === id);
      if (!content) {
        return null;
      }
      return {
        contentType,
        ...content,
      };
    };

    return sections.map(({content, ...rest}) => {
      const enrichedContent = content.map(getContentData).filter((c) => !!c);
      return {
        ...rest,
        content: enrichedContent,
      };
    });
  }
);
